import { components } from "@/api-schema";
import { TemporaryPermissionStatus } from "@/api-schema-enums";
import { formatDateToLocale } from "@/common/helpers/format-date";
import { BaseSubComponent } from "@/components/data-table/predefined/_components/base-sub-component";
import { SubComponentInfoCard } from "@/components/data-table/predefined/_components/sub-component-info-card";
import { DataTableRow } from "@/components/data-table/types";
import { UserItem, UserItemVariant } from "@/components/user-item/user-item";
import { CalendarCheckIcon, CalendarXIcon, MessageSquareTextIcon, UserCheckIcon, UserXIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

type TemporaryPermission = components["schemas"]["TemporaryPermission"];

const DECISION_DISPLAY = {
  [TemporaryPermissionStatus.Approved]: {
    atLabel: "ram:ApprovedAt",
    atIcon: CalendarCheckIcon,
    byLabel: "ram:ApprovedBy",
    byIcon: UserCheckIcon,
  },
  [TemporaryPermissionStatus.Rejected]: {
    atLabel: "ram:RejectedAt",
    atIcon: CalendarXIcon,
    byLabel: "ram:RejectedBy",
    byIcon: UserXIcon,
  },
} as const;

export interface TemporaryPermissionSubComponentProps {
  row: DataTableRow<TemporaryPermission>;
}

export function TemporaryPermissionSubComponent({ row }: TemporaryPermissionSubComponentProps) {
  const { t } = useTranslation(["ram", "common"]);
  const { uuid, reason, status, decided_at, decided_by, createdAt, updatedAt } = row.original;
  const decision =
    status === undefined || status === TemporaryPermissionStatus.Pending ? undefined : DECISION_DISPLAY[status];

  return (
    <BaseSubComponent uuid={uuid} createdAt={createdAt} updatedAt={updatedAt}>
      <SubComponentInfoCard icon={MessageSquareTextIcon} label={t("ram:Reason")} value={reason} />
      {decision && decided_at && (
        <SubComponentInfoCard
          icon={decision.atIcon}
          label={t(decision.atLabel)}
          value={formatDateToLocale(decided_at)}
        />
      )}
      {decision && decided_by && (
        <SubComponentInfoCard
          icon={decision.byIcon}
          label={t(decision.byLabel)}
          value={<UserItem user={decided_by} variant={UserItemVariant.Compact} />}
        />
      )}
    </BaseSubComponent>
  );
}
